/* Native modal handles inert background, Escape and the focus boundary. */
class PaCollectionsDrawer extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-collections-open]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    if (!this.dialog?.showModal || !this.tabs.length) return;
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.opener.setAttribute('aria-haspopup', 'dialog');
    this.opener.setAttribute('aria-controls', this.dialog.id);
    this.opener.setAttribute('aria-expanded', 'false');
    this.opener.addEventListener('click', (event) => {
      event.preventDefault();
      this.dialog.classList.remove('pa-collections__dialog--detail');
      this.previousOverflow = document.body.style.overflow;
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
      this.opener.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-collections-close]').focus();
    }, options);
    for (const button of this.querySelectorAll('[data-collections-close]')) {
      button.addEventListener('click', () => this.dialog.close(), options);
    }
    this.dialog.addEventListener('close', () => {
      document.body.style.overflow = this.previousOverflow ?? '';
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus();
    }, options);
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]')]
        .filter(element => element.tabIndex >= 0 && (element.checkVisibility ? element.checkVisibility() : element.getClientRects().length));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }, options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, options);
    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.select(tab, true), options);
      tab.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(tab);
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.select(this.tabs[next], false);
        this.tabs[next].focus();
      }, options);
    }
    this.querySelector('[data-collections-back]').addEventListener('click', () => {
      this.dialog.classList.remove('pa-collections__dialog--detail');
      this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true').focus();
    }, options);
    this.mobile.addEventListener('change', () => {
      if (!this.dialog.open) return;
      this.dialog.classList.remove('pa-collections__dialog--detail');
      this.querySelector('[data-collections-close]').focus();
    }, options);
  }

  /** @param {HTMLButtonElement} selected @param {boolean} enter */
  select(selected, enter) {
    for (const tab of this.tabs) {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const panel of this.panels) {
      panel.hidden = panel.id !== selected.getAttribute('aria-controls');
    }
    if (enter && this.mobile.matches) {
      this.dialog.classList.add('pa-collections__dialog--detail');
      this.querySelector('[data-collections-back]').focus();
      this.querySelector('.pa-collections__panels').scrollTop = 0;
    }
  }

  disconnectedCallback() {
    if (this.dialog?.open) {
      this.dialog.close();
      document.body.style.overflow = this.previousOverflow ?? '';
    }
    this.controller?.abort();
    this.controller = null;
  }
}
if (!customElements.get('pa-collections-drawer')) customElements.define('pa-collections-drawer', PaCollectionsDrawer);
